import React, {useState, useEffect, useMemo} from 'react';
import {
    Button,
    Table,
    Input,
    Form,
    Typography,
    message,
    Layout,
    Row,
    Col,
    Card,
    Space,
    Modal,
    Transfer,
    Popconfirm,
    Radio, RadioChangeEvent, Dropdown
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    PlusOutlined,
    EditOutlined,
    TeamOutlined,
    DownOutlined, DownloadOutlined
} from '@ant-design/icons';
import CommitteePdfViewer from './components/CommitteePdfViewer';
import {
    CommitteeCreateCommitteeMembersRequest,
    CommitteeCreateCommitteesRequest,
    CommitteeDeleteCommitteeMembersRequest, CommitteeDeleteCommitteesRequest, CommitteeGetCandidateMembersRequest,
    CommitteeSearchRequest,
    CommitteeSearchResponse, CommitteeUpdateCommitteesRequest
} from "../../model";
import {committeeService, useAuth} from "../../services";
import {getEditableRoles, getPrintableRoles} from "../../utils/rolePermissions";
import {useLocation} from "react-router-dom";
import {reportService} from "../../services/domain/report/service";
import {IdentityTypeModalPops} from "../../model";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const {Title} = Typography;
const {Content} = Layout;

const CommitteePage: React.FC = () => {
    const {hasEditPermission, hasPrintPermission} = useAuth();
    const location = useLocation();
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [committees, setCommittees] = useState<CommitteeSearchResponse[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [currentCommittee, setCurrentCommittee] = useState<CommitteeSearchResponse | null>(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // 詳情對話框相關狀態
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [selectedCommittee, setSelectedCommittee] = useState<{
        committeeId: number;
        committeeName: string;
        members: any[];
    } | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Transfer 組件相關狀態
    const [searchForm] = Form.useForm();
    const [transferData, setTransferData] = useState<any[]>([]); // 所有可能的會員數據
    const [targetKeys, setTargetKeys] = useState<string[]>([]); // 已分配到委員會的會員 ID
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]); // 當前選中的會員 ID
    const [searchLoading, setSearchLoading] = useState(false);

    // 搜尋專委會
    const handleSearch = async (values: any) => {
        setLoading(true);
        try {
            // 構建搜尋請求 - 確保所有欄位為空時傳送空字串而非 undefined
            const searchRequest: CommitteeSearchRequest = {
                committeeName: values.committeeName || '',
            };

            // 調用API搜尋專委會
            const result = await committeeService.search(searchRequest);

            if (result) {
                setCommittees(result);
                setTotalRecords(result.length);
                // 重置到第一頁
                setPagination(resetPaginationByTotal(result.length));

                // 如果沒有返回數據，顯示通知
                if (result.length === 0) {
                    message.info('查無資料');
                }
            } else {
                // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
                setCommittees([]);
                setTotalRecords(0);
                message.error('查詢失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋專委會失敗:', error);
            message.error('搜尋失敗，請稍後重試');
            setCommittees([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // 處理分頁、篩選和排序變更
    const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
        const newPagination = {
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        };

        setPagination(newPagination);
    };

    // 重置表單
    const handleReset = () => {
        form.resetFields();
    };

    // 顯示新增專委會對話框
    const showCreateModal = () => {
        createForm.resetFields();
        setIsCreateModalVisible(true);
    };

    // 關閉新增專委會對話框
    const handleCreateCancel = () => {
        setIsCreateModalVisible(false);
    };

    // 處理新增專委會
    const handleCreate = async () => {
        try {
            const values = await createForm.validateFields();
            setCreateLoading(true);

            const createRequest: CommitteeCreateCommitteesRequest = {
                committees: [{
                    committeeName: values.committeeName,
                    createdBy: 'tami'
                }]
            };

            const result = await committeeService.create(createRequest);

            if (result && result.createdCount > 0) {
                message.success('專委會新增成功');
                setIsCreateModalVisible(false);
                // 重新載入資料
                handleSearch({});
            } else {
                message.error('專委會新增失敗');
            }
        } catch (error) {
            console.error('新增專委會時發生錯誤:', error);
            message.error('表單驗證失敗或新增過程發生錯誤');
        } finally {
            setCreateLoading(false);
        }
    };

    // 處理查看專委會詳情
    const handleViewDetail = async (record: CommitteeSearchResponse) => {
        setDetailLoading(true);
        try {
            // 設置專委會基本資訊
            setSelectedCommittee({
                committeeId: record.committeeId,
                committeeName: record.committeeName,
                members: []
            });

            setIsDetailModalVisible(true);

            // 重置搜尋表單
            searchForm.resetFields();

            // 獲取專委會已分配的會員
            const committeeRequest = {
                committeeId: record.committeeId
            };

            const committeeMembers = await committeeService.getCommitteeMembers(committeeRequest);

            if (committeeMembers) {
                // 更新專委會資訊中的會員列表
                const membersList = committeeMembers.map(member => ({
                    membershipId: member.committeeMemberId,
                    memberId: member.memberId,
                    companyName: member.companyName
                }));

                setSelectedCommittee(prev => prev ? {
                    ...prev,
                    members: membersList
                } : null);

                // 設置 Transfer 的右側面板數據（已分配會員）
                setTransferData([
                    // 已分配的會員加入，以便顯示在右側面板
                    ...committeeMembers.map(member => ({
                        key: member.committeeMemberId.toString(),
                        title: `${member.memberId} - ${member.companyName}`,
                        description: '',
                        membershipId: member.membershipId,
                        memberId: member.memberId,
                        companyName: member.companyName
                    }))
                ]);

                // 設置已分配的會員至右側面板
                setTargetKeys(committeeMembers.map(member =>
                    member.committeeMemberId.toString()
                ));

                setSelectedKeys([]);
            } else {
                message.error('無法載入專委會會員');
            }
        } catch (error) {
            console.error('載入專委會詳情失敗:', error);
            message.error('載入專委會詳情失敗');
        } finally {
            setDetailLoading(false);
        }
    };

    // 關閉詳情對話框
    const handleDetailCancel = () => {
        setIsDetailModalVisible(false);
        setSelectedCommittee(null);
    };

    // 保存委員會會員分配
    const [, setSaveLoading] = useState(false);
    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
    const [selectedMembershipIds, setSelectedMembershipIds] = useState<number[]>([]);
    // const [identityTypeModalVisible, setIdentityTypeModalVisible] = useState(false);
    const [identityTypeModalPops, setIdentityTypeModalProps] = useState<IdentityTypeModalPops>({
        actionKey: '',
        visible: false
    });
    const [previewPdfLanguageModalVisible, setPreviewPdfLanguageModalVisible] = useState(false);
    const [printLanguage, setPrintLanguage] = useState("ZH_TW");
    const identityTypeOptions = [
        {label: '負責人', value: 'RESPONSIBLE_PERSON'},
        {label: '董事長', value: 'CHAIRMAN'},
        {label: '總經理', value: 'GENERAL_MANAGER'},
        {label: '會籍聯絡人', value: 'MEMBERSHIP_CONTACT'},
        {label: '專委會聯絡人', value: 'COMMITTEE_CONTACT'},
        {label: '展覽會聯絡人', value: 'EXHIBITION_CONTACT'}
    ];
    const [identityType, setIdentityType] = useState(identityTypeOptions[0]?.value || '');

    // 處理會員轉移
    const handleMembershipChange = async (newTargetKeys: React.Key[], direction: string, moveKeys: React.Key[]) => {
        // 更新右側面板的已選會員
        setTargetKeys(newTargetKeys as string[]);

        if (!selectedCommittee) return;

        try {
            // 如果是新增會員 (從左到右)
            if (direction === 'right' && moveKeys.length > 0) {
                setSaveLoading(true);

                // 獲取要新增的會員資料
                const membersToAdd = transferData
                    .filter(item => moveKeys.includes(item.key))
                    .map(item => ({
                        committeeId: selectedCommittee.committeeId,
                        membershipId: item.membershipId,
                        createdBy: 'tami'
                    }));

                // 呼叫API新增會員
                const createRequest: CommitteeCreateCommitteeMembersRequest = {
                    committeeMembers: membersToAdd
                };

                const result = await committeeService.createCommitteeMembers(createRequest);

                if (result) {
                    message.success(`已成功新增 ${result.createdCount} 位會員`);

                    // 重新載入專委會會員以獲取正確的 committeeMemberId
                    await handleViewDetail({
                        committeeId: selectedCommittee.committeeId,
                        committeeName: selectedCommittee.committeeName
                    });
                } else {
                    message.error('新增會員失敗');
                }
            }
            // 如果是移除會員 (從右到左)
            else if (direction === 'left' && moveKeys.length > 0) {
                setSaveLoading(true);

                // 獲取要移除的會員 ID
                const membersToRemove = moveKeys.map(key => ({
                    committeeMemberId: parseInt(key.toString(), 10)
                }));

                // 呼叫API刪除會員
                const deleteRequest: CommitteeDeleteCommitteeMembersRequest = {
                    committeeMemberIds: membersToRemove
                };

                const result = await committeeService.deleteCommitteeMembers(deleteRequest);

                if (result) {
                    message.success(`已成功移除 ${result.deletedCount} 位會員`);

                    // 重新載入專委會會員
                    await handleViewDetail({
                        committeeId: selectedCommittee.committeeId,
                        committeeName: selectedCommittee.committeeName
                    });
                } else {
                    message.error('移除會員失敗');
                }
            }
        } catch (error) {
            console.error('更新專委會會員失敗:', error);
            message.error('操作失敗，請稍後重試');
        } finally {
            setSaveLoading(false);
        }
    };

    // 處理會員選擇
    const handleMembershipSelectChange = (sourceSelectedKeys: React.Key[], targetSelectedKeys: React.Key[]) => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys] as string[]);
    };

    // 處理列印功能
    const getSelectedMembershipIds = () => {
        return selectedKeys
            .filter(key => targetKeys.includes(key.toString()))
            .map(key => {
                // 從transferData中找到對應的會員資料
                const memberData = transferData.find(item => item.key === key);
                return memberData ? memberData.membershipId : null;
            })
            .filter(id => id !== null) as number[]; // 過濾掉可能的null值
    }

    const handleTest = (actionKey: string) => {
        // 獲取已選中的委員會會員IDs (選中的且已在右側的)
        const membershipIds = getSelectedMembershipIds();
        if (membershipIds.length === 0) {
            message.warning('請選擇至少一位會員');
            return;
        }
        // 設置選擇的會員ID並顯示PDF預覽器
        setSelectedMembershipIds(membershipIds);
        setIdentityTypeModalProps({
            actionKey: actionKey,
            visible: true
        });
    }

    // 執行候選會員搜尋，更新左側面板的數據
    const handleMemberSearch = async (values: any) => {
        if (!selectedCommittee) return;

        // 檢查是否至少提供了產品代號範圍或會員編號範圍之一
        if (!values.productCodeStart && !values.productCodeEnd &&
            !values.memberIdStart && !values.memberIdEnd) {
            message.warning('請至少提供產品代號範圍或會員編號範圍');
            return;
        }

        setSearchLoading(true);
        try {
            // 構建候選會員搜尋請求
            const searchRequest: CommitteeGetCandidateMembersRequest = {
                memberIdStart: values.memberIdStart,
                memberIdEnd: values.memberIdEnd,
                productCodeStart: values.productCodeStart,
                productCodeEnd: values.productCodeEnd
            };

            // 使用 getCandidateMembers API 取得符合條件的候選會員
            const candidateMembers = await committeeService.getCandidateMembers(searchRequest);

            if (candidateMembers) {
                // 取得該委員會目前的成員
                const committeeRequest = {
                    committeeId: selectedCommittee.committeeId
                };

                const committeeMembers = await committeeService.getCommitteeMembers(committeeRequest);

                if (committeeMembers) {
                    // 取得已經分配的會員ID列表
                    const committeeCommitteeMemberIds = committeeMembers.map(member => member.committeeMemberId);
                    const committeeMemberIds = committeeMembers.map(member => member.memberId);

                    // 過濾出未分配的會員（客戶端過濾）
                    const availableMemberships = candidateMembers.filter(member =>
                        !committeeMemberIds.includes(member.memberId)
                    );

                    // 設置 Transfer 的左側面板數據（可選會員）
                    const leftPaneData = availableMemberships.map(member => ({
                        key: member.membershipId.toString(),
                        title: `${member.memberId} - ${member.companyName}`,
                        description: '',
                        membershipId: member.membershipId,
                        memberId: member.memberId,
                        companyName: member.companyName
                    }));

                    // 右側面板的數據（已分配會員）
                    const rightPaneData = committeeMembers.map(member => ({
                        key: member.committeeMemberId.toString(),
                        title: `${member.memberId} - ${member.companyName}`,
                        description: '',
                        membershipId: member.committeeMemberId,
                        memberId: member.memberId,
                        companyName: member.companyName
                    }));

                    // 合併左右側數據，用作 Transfer 的完整數據來源
                    setTransferData([...leftPaneData, ...rightPaneData]);

                    // 設置已分配的會員至右側面板
                    setTargetKeys(committeeCommitteeMemberIds.map(id => id.toString()));

                    // 如果沒有找到符合條件的未分配會員，顯示提示
                    if (availableMemberships.length === 0) {
                        message.info('查無符合條件的會員');
                    }
                }
            } else {
                message.error('搜尋候選會員失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋候選會員失敗:', error);
            message.error('搜尋候選會員失敗，請稍後重試');
        } finally {
            setSearchLoading(false);
        }
    };

    // 初始加載
    useEffect(() => {
        // 初始搜尋所有專委會
        // handleSearch({});
    }, []);

    // 處理編輯專委會
    const handleEdit = (record: CommitteeSearchResponse) => {
        setCurrentCommittee(record);
        editForm.setFieldsValue({
            committeeName: record.committeeName
        });
        setIsEditModalVisible(true);
    };

    // 關閉編輯專委會對話框
    const handleEditCancel = () => {
        setIsEditModalVisible(false);
        setCurrentCommittee(null);
    };

    // 提交編輯專委會
    const handleEditSubmit = async () => {
        if (!currentCommittee) return;

        try {
            const values = await editForm.validateFields();
            setEditLoading(true);

            // 構建編輯請求
            const editRequest: CommitteeUpdateCommitteesRequest = {
                committees: [{
                    committeeId: currentCommittee.committeeId,
                    committeeName: values.committeeName
                }]
            };

            const result = await committeeService.update(editRequest);

            if (result && result.updatedCount > 0) {
                message.success('專委會修改成功');
                setIsEditModalVisible(false);
                // 重新載入資料
                handleSearch({});
            } else {
                message.error('專委會修改失敗');
            }
        } catch (error) {
            console.error('修改專委會時發生錯誤:', error);
            message.error('表單驗證失敗或修改過程發生錯誤');
        } finally {
            setEditLoading(false);
        }
    };

    const handleIdentityTypeChange = ({target: {value}}: RadioChangeEvent) => {
        setIdentityType(value);
    };

    // 處理刪除專委會
    const handleDelete = async (record: CommitteeSearchResponse) => {
        try {
            const deleteRequest: CommitteeDeleteCommitteesRequest = {
                committeeIds: [{
                    committeeId: record.committeeId
                }]
            };

            const result = await committeeService.delete(deleteRequest);

            if (result && result.deletedCount > 0) {
                message.success('專委會刪除成功');
                // 重新載入資料
                handleSearch({});
            } else {
                message.error('專委會刪除失敗');
            }
        } catch (error) {
            console.error('刪除專委會時發生錯誤:', error);
            message.error('刪除過程發生錯誤');
        }
    };

    const apiParam = useMemo(() => {
        console.log(`generateApiParam pdfViewerVisible: ${pdfViewerVisible}`)
        return {
            membershipIds: selectedMembershipIds.map(key => ({membershipId: key.toString()})),
            language: printLanguage,
            identityType: identityType
        };
    }, [selectedMembershipIds, printLanguage, identityType, pdfViewerVisible]);

    // 表格列定義
    const columns = [
        {
            title: '專委會ID',
            dataIndex: 'committeeId',
            key: 'committeeId',
            width: 120
        },
        {
            title: '專委會名稱',
            dataIndex: 'committeeName',
            key: 'committeeName'
        },
        {
            title: '操作',
            key: 'action',
            width: 250,
            render: (text: string, record: CommitteeSearchResponse) => (
                <Space>
                    <Button
                        icon={<TeamOutlined/>}
                        onClick={() => handleViewDetail(record)}
                        title="會員分配"
                    >
                        會員分配
                    </Button>
                    <Button
                        icon={<EditOutlined/>}
                        onClick={() => handleEdit(record)}
                        title="編輯"
                        disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                    />
                    <Popconfirm
                        title="確定要刪除此專委會嗎？"
                        onConfirm={() => handleDelete(record)}
                        okText="確定"
                        cancelText="取消"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined/>}
                            title="刪除"
                            disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <Layout style={{minHeight: '100%'}}>
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" style={{marginBottom: 20}}>
                    <Col>
                        <Title level={3}>
                            <span style={{marginRight: 8}}>👥</span> 專委會
                        </Title>
                    </Col>
                </Row>

                {/* 搜尋表單 */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined/>
                            <span>搜尋條件</span>
                        </Space>
                    }
                    style={{marginBottom: 20}}
                >
                    <Form
                        form={form}
                        name="committeeSearch"
                        onFinish={handleSearch}
                        layout="vertical"
                    >
                        <Row gutter={[24, 0]}>
                            {/* 專委會名稱 */}
                            <Col xs={24}>
                                <Form.Item label="專委會名稱" name="committeeName">
                                    <Input
                                        placeholder="輸入專委會名稱"
                                        prefix={<span style={{marginRight: 6}}>🏢</span>}
                                    />
                                </Form.Item>
                            </Col>

                            {/* 按鈕區域 */}
                            <Col xs={24}>
                                <Row justify="center" gutter={[16, 0]}>
                                    <Col>
                                        <Button
                                            onClick={handleReset}
                                            icon={<DeleteOutlined/>}
                                        >
                                            清除
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SearchOutlined/>}
                                        >
                                            查詢
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* 搜尋結果區域 */}
                <Card
                    title={
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span>搜尋結果: {totalRecords} 筆記錄</span>
                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                onClick={showCreateModal}
                                disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                            >
                                新增專委會
                            </Button>
                        </div>
                    }
                >
                    <Table
                        virtual
                        scroll={{ y: 600 }}
                        rowKey="committeeId"
                        dataSource={committees}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            total: totalRecords,
                            showSizeChanger: true,
                            pageSizeOptions: getPageSizeOptions(totalRecords),
                            showTotal: (total) => `共 ${total} 筆`
                        }}
                        onChange={handleTableChange}
                    />
                </Card>

                {/* 新增專委會對話框 */}
                <Modal
                    title="新增專委會"
                    open={isCreateModalVisible}
                    onCancel={handleCreateCancel}
                    footer={[
                        <Button key="cancel" onClick={handleCreateCancel}>
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={createLoading}
                            onClick={handleCreate}
                        >
                            確定
                        </Button>,
                    ]}
                >
                    <Form
                        form={createForm}
                        layout="vertical"
                        name="createCommitteeForm"
                    >
                        <Form.Item
                            name="committeeName"
                            label="專委會名稱"
                            rules={[
                                {required: true, message: '請輸入專委會名稱'},
                                {max: 50, message: '專委會名稱不能超過50個字元'}
                            ]}
                        >
                            <Input placeholder="請輸入專委會名稱"/>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* 編輯專委會對話框 */}
                <Modal
                    title="修改專委會名稱"
                    open={isEditModalVisible}
                    onCancel={handleEditCancel}
                    footer={[
                        <Button key="cancel" onClick={handleEditCancel}>
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={editLoading}
                            onClick={handleEditSubmit}
                        >
                            確定
                        </Button>,
                    ]}
                >
                    <Form
                        form={editForm}
                        layout="vertical"
                        name="editCommitteeForm"
                    >
                        <Form.Item
                            name="committeeName"
                            label="專委會名稱"
                            rules={[
                                {required: true, message: '請輸入專委會名稱'},
                                {max: 50, message: '專委會名稱不能超過50個字元'}
                            ]}
                        >
                            <Input placeholder="請輸入專委會名稱"/>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* 專委會詳情對話框 */}
                <Modal
                    title={`專委會詳情: ${selectedCommittee?.committeeName || ''}`}
                    open={isDetailModalVisible}
                    onCancel={handleDetailCancel}
                    width={800}
                    footer={(
                        <Row justify="end" gutter={[8, 0]}>
                            <Col>
                                <Space.Compact>
                                    <Button onClick={handleDetailCancel}>
                                        關閉
                                    </Button>
                                    {selectedKeys.filter(key => targetKeys.includes(key)).length > 0 && (
                                        <>
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {
                                                            key: 'print',
                                                            label: '會員郵寄名條列表',
                                                            icon: (<DownloadOutlined/>)
                                                        }
                                                    ],
                                                    onClick: ({key}) => {
                                                        handleTest(key);
                                                    }
                                                }}
                                                disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                trigger={['click', 'hover']}
                                            >
                                                <Button
                                                    icon={<DownOutlined/>}
                                                    disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                >
                                                    列印選項
                                                </Button>
                                            </Dropdown>
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {
                                                            key: 'exportCommitteCsv',
                                                            label: '會員資料',
                                                            icon: (<DownloadOutlined/>)
                                                        },
                                                        {
                                                            key: 'exportCommitteTxt',
                                                            label: '電子郵件',
                                                            icon: (<DownloadOutlined/>)
                                                        },
                                                    ],
                                                    onClick: ({key}) => {
                                                        handleTest(key);
                                                    }
                                                }}
                                                disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                trigger={['click', 'hover']}
                                            >
                                                <Button
                                                    icon={<DownOutlined/>}
                                                    disabled={targetKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}
                                                >
                                                    下載
                                                </Button>
                                            </Dropdown>
                                        </>
                                    )}
                                </Space.Compact>
                            </Col>
                        </Row>
                    )}
                >
                    {detailLoading ? (
                        <div style={{textAlign: 'center', padding: '20px'}}>載入中...</div>
                    ) : (
                        <>
                            {/* 搜尋表單 */}
                            <Card
                                title="搜尋會員"
                                size="small"
                                style={{marginBottom: 16}}
                            >
                                <Form
                                    form={searchForm}
                                    layout="vertical"
                                    onFinish={handleMemberSearch}
                                >
                                    <Row gutter={[16, 16]}>
                                        {/* 產品代號範圍 */}
                                        <Col xs={24} md={12}>
                                            <Form.Item label="產品代號範圍" style={{marginBottom: 0}}>
                                                <Input.Group compact>
                                                    <Form.Item
                                                        name="productCodeStart"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="起始代號"
                                                        />
                                                    </Form.Item>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '10%',
                                                        textAlign: 'center'
                                                    }}>~</span>
                                                    <Form.Item
                                                        name="productCodeEnd"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="結束代號"
                                                        />
                                                    </Form.Item>
                                                </Input.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* 會員證號範圍 */}
                                        <Col xs={24} md={12}>
                                            <Form.Item label="會員證號範圍" style={{marginBottom: 0}}>
                                                <Input.Group compact>
                                                    <Form.Item
                                                        name="memberIdStart"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="起始證號"
                                                        />
                                                    </Form.Item>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '10%',
                                                        textAlign: 'center'
                                                    }}>~</span>
                                                    <Form.Item
                                                        name="memberIdEnd"
                                                        noStyle
                                                    >
                                                        <Input
                                                            style={{width: '45%'}}
                                                            placeholder="結束證號"
                                                        />
                                                    </Form.Item>
                                                </Input.Group>
                                            </Form.Item>
                                        </Col>

                                        {/* 按鈕 */}
                                        <Col xs={24} style={{display: 'flex', justifyContent: 'flex-end'}}>
                                            <Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={searchLoading}
                                                    icon={<SearchOutlined/>}
                                                >
                                                    搜尋
                                                </Button>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card>

                            {/* 會員轉移組件 */}
                            {/*datasource: 提供所有可選擇的數據項目，即 Transfer 左側列表的內容。*/}
                            {/*targetKeys: 定義哪些 dataSource 項目應該被移動到 右側（已選擇區）。*/}
                            {/*selectedKeys: 用於追蹤 當前被選中的項目（無論左側還是右側）。*/}
                            <div>
                                <Transfer
                                    dataSource={transferData}
                                    titles={['符合搜尋條件的會員', '專委會會員']}
                                    targetKeys={targetKeys}
                                    selectedKeys={selectedKeys}
                                    onChange={(newTargetKeys, direction, moveKeys) =>
                                        handleMembershipChange(newTargetKeys, direction, moveKeys)
                                    }
                                    onSelectChange={handleMembershipSelectChange}
                                    render={item => `${item.memberId} - ${item.companyName}`}
                                    listStyle={{
                                        width: 330,
                                        height: 400,
                                    }}
                                    filterOption={(inputValue, item) =>
                                        item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1 ||
                                        (item.description && item.description.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1)
                                    }
                                    showSearch
                                    locale={{
                                        searchPlaceholder: '搜尋會員資訊...',
                                        notFoundContent: '無符合條件的會員'
                                    }}
                                />
                            </div>
                        </>
                    )}
                </Modal>

                <Modal
                    title="請選擇列印中文或英文"
                    open={previewPdfLanguageModalVisible}
                    onCancel={() => {
                        setPrintLanguage("EN_US");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onCancel')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    onOk={() => {
                        setPrintLanguage("ZH_TW");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onOk')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    okText="中文"
                    cancelText="英文"
                >
                </Modal>

                <Modal
                    title="請選擇身份別"
                    open={identityTypeModalPops.visible}
                    onCancel={() => {
                        setIdentityType(identityTypeOptions[0]?.value || '');
                        setIdentityTypeModalProps({
                            actionKey: '',
                            visible: false
                        })
                    }}
                    onOk={() => {
                        if (identityTypeModalPops.actionKey === 'print') {
                            setPreviewPdfLanguageModalVisible(true);
                        }

                        if (identityTypeModalPops.actionKey === 'exportCommitteCsv') {
                            reportService.exportCommitteCsv({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        }
                        if (identityTypeModalPops.actionKey === 'exportCommitteTxt') {
                            reportService.exportCommitteTxt({
                                membershipIds: apiParam.membershipIds,
                                type: apiParam.identityType
                            });
                        }
                        setIdentityType(identityTypeOptions[0]?.value || '');
                        setIdentityTypeModalProps({
                            actionKey: '',
                            visible: false
                        })
                    }}
                >
                    <Radio.Group options={identityTypeOptions}
                                 onChange={handleIdentityTypeChange} value={identityType}/>
                </Modal>
            </Content>

            {/* PDF預覽器組件 */}
            {selectedCommittee && (
                <CommitteePdfViewer
                    visible={pdfViewerVisible}
                    onClose={() => setPdfViewerVisible(false)}
                    committeeId={selectedCommittee.committeeId}
                    committeeName={selectedCommittee.committeeName}
                    membershipIds={selectedMembershipIds}
                    apiParam={apiParam}
                />
            )}
        </Layout>
    );
};

export default CommitteePage;